import "./style.scss";

import { Skeleton } from "@mui/material";
import type { ReactNode } from "react";
import { Fragment, useState } from "react";

export type BasicTableAfterRow = {
    leading: ReactNode;
    content: ReactNode;
};

export interface BasicTableColumn<T> {
    header: string;
    /** `index` is the position among currently displayed rows; `rows` is the full prop array (not the capped slice). */
    render: (row: T, index: number, rows: T[]) => ReactNode;
    align?: "left" | "right" | "center";
    /** Header alignment; defaults to `align`, then `left`. */
    headerAlign?: "left" | "right" | "center";
    headerClassName?: string;
    cellClassName?: string;
}

interface IBasicTable<T> {
    columns: BasicTableColumn<T>[];
    rows: T[];
    /** Extra CSS class on the wrapper div. */
    className?: string;
    /** Show skeleton rows while data is loading. */
    isLoading?: boolean;
    /** Number of skeleton rows to display while loading. Defaults to 3. */
    skeletonRows?: number;
    /**
     * Limit the number of initially visible rows.
     * A "show all / reduce" toggle button is rendered below the table.
     */
    maxRows?: number;
    /** Label for the "expand" button. Defaults to "Voir tout". */
    showAllLabel?: string;
    /** Label for the "collapse" button. Defaults to "Réduire". */
    hideLabel?: string;
    /**
     * Optional title rendered as a spanning header row at the top of the table,
     * visually integrated into the table (not a separate element above it).
     */
    caption?: string;
    /** Optional class name per data row (e.g. highlight). */
    getRowClassName?: (row: T, index: number) => string | undefined;
    /**
     * Extra rows under a data row. The leading cell stays in column 1; the content starts at column 2.
     * Return null when the row has nothing to show.
     */
    renderAfterRow?: (row: T, index: number, rows: T[]) => BasicTableAfterRow | BasicTableAfterRow[] | null;
}

/**
 * Generic lightweight table for recap / summary displays.
 * Supports loading skeletons, optional row capping with a show/hide toggle.
 */
const BasicTable = <T,>({
    columns,
    rows,
    className,
    isLoading = false,
    skeletonRows = 3,
    maxRows,
    showAllLabel = "Voir tout",
    hideLabel = "Réduire",
    caption,
    getRowClassName,
    renderAfterRow,
}: IBasicTable<T>) => {
    const [expanded, setExpanded] = useState(false);

    const visibleRows = maxRows && !expanded && rows.length > maxRows ? rows.slice(0, maxRows) : rows;

    const showToggle = !isLoading && maxRows !== undefined && rows.length > maxRows;

    return (
        <div className={`basic-table-wrapper ${className ?? ""}`}>
            <table className="basic-table">
                <thead>
                    {caption && (
                        <tr>
                            <th colSpan={columns.length} className="basic-table-caption">
                                {caption}
                            </th>
                        </tr>
                    )}
                    <tr>
                        {columns.map((col, i) => (
                            <th
                                key={i}
                                className={col.headerClassName}
                                style={{ textAlign: col.headerAlign ?? col.align ?? "left" }}
                            >
                                {col.header}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {isLoading
                        ? Array.from({ length: skeletonRows }).map((_, i) => (
                              <tr key={i}>
                                  {columns.map((_, j) => (
                                      <td key={j}>
                                          <Skeleton variant="text" width="80%" />
                                      </td>
                                  ))}
                              </tr>
                          ))
                        : visibleRows.map((row, i) => {
                              const afterRow = renderAfterRow?.(row, i, rows);
                              const afterRows = afterRow == null ? [] : Array.isArray(afterRow) ? afterRow : [afterRow];
                              return (
                                  <Fragment key={i}>
                                      <tr className={getRowClassName?.(row, i)}>
                                          {columns.map((col, j) => (
                                              <td
                                                  key={j}
                                                  className={col.cellClassName}
                                                  style={{ textAlign: col.align ?? "left" }}
                                              >
                                                  {col.render(row, i, rows)}
                                              </td>
                                          ))}
                                      </tr>
                                      {afterRows.map((notice, noticeIndex) => (
                                          <tr key={`after-${noticeIndex}`} className="basic-table-after-row">
                                              <td className="basic-table-after-row__leading">{notice.leading}</td>
                                              <td
                                                  className="basic-table-after-row__content"
                                                  colSpan={Math.max(columns.length - 1, 1)}
                                              >
                                                  {notice.content}
                                              </td>
                                          </tr>
                                      ))}
                                  </Fragment>
                              );
                          })}
                </tbody>
            </table>

            {showToggle && (
                <button type="button" className="basic-table-show-all" onClick={() => setExpanded((prev) => !prev)}>
                    {expanded ? hideLabel : `${showAllLabel} (${rows.length})`}
                </button>
            )}
        </div>
    );
};

export default BasicTable;
